import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { formatDistanceToNow } from "date-fns";

import { faPaperclip } from "@fortawesome/pro-regular-svg-icons";

import { StaffTicket } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { Table, TableColumn } from "@/components/Table/Table";
import { ROUTE_CONFIG } from "@/routes/config";

import { TicketCategoryPill } from "../../Support/components/TicketPills";
import { StaffTicketQueueController } from "../hooks/staffTicketQueueController";
import { toQueueRouteState } from "../pages/TicketDetail/utils/ticketNeighbours";
import { ageParts } from "../utils/staffTicketFormat";
import {
	staffLastActivity,
	ticketSenderName,
	ticketUserInitials,
	ticketUserName,
} from "../utils/staffTicketRowMeta";
import { StaffTicketStatusPill, SupportUiOffMarker, TicketPriorityBadge } from "./StaffTicketPills";

/**
 * The queue rows (mockup `.tbl`). The page arrives sorted and filtered by the API and is rendered
 * as is; paging goes through the shared Table's page controls. A row of a company without the
 * customer UI carries the "UI off" marker (spec §8). The row click opens `/app/tickets/:id` with
 * the page's ids in router state, so the detail's Previous / Next can step through this page
 * (PREC-3940 decision 4).
 */
export const StaffTicketsTable: FunctionComponent<{ controller: StaffTicketQueueController }> = ({
	controller,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const now = new Date();

	const columns: TableColumn[] = [
		{
			title: t("support.staff.columns.ticket"),
			width: "130px",
			render: (ticket: StaffTicket) => (
				<span className="flex items-center gap-2">
					<span className="text-sm tabular-nums text-text-meta">#{ticket.id}</span>
					{!controller.isSupportUiActive(ticket.companyId) && <SupportUiOffMarker />}
				</span>
			),
		},
		{
			title: t("support.staff.columns.priority"),
			width: "96px",
			render: (ticket: StaffTicket) => <TicketPriorityBadge priority={ticket.priority} />,
		},
		{
			title: t("support.staff.columns.subject"),
			render: (ticket: StaffTicket) => {
				// A SQL sub-query count: it arrives as a string (web CLAUDE.md §6).
				const files = Number(ticket.attachmentCount ?? 0);

				return (
					<div className="flex min-w-0 flex-col gap-0.5">
						<span className="truncate text-sm font-semibold text-text">{ticket.subject}</span>
						<span className="flex items-center gap-1.5 truncate text-xs text-text-meta">
							<span className="font-semibold">{ticket.companyName ?? "—"}</span>
							<span>·</span>
							<span>{ticketSenderName(ticket)}</span>
							{files > 0 && (
								<>
									<span>·</span>
									<span className="inline-flex items-center gap-1">
										<Icon icon={faPaperclip} />
										{files}
									</span>
								</>
							)}
						</span>
					</div>
				);
			},
		},
		{
			title: t("support.staff.columns.category"),
			width: "140px",
			render: (ticket: StaffTicket) => <TicketCategoryPill category={ticket.category} />,
		},
		{
			title: t("support.staff.columns.status"),
			width: "170px",
			render: (ticket: StaffTicket) => <StaffTicketStatusPill status={ticket.status} />,
		},
		{
			title: t("support.staff.columns.assignee"),
			width: "150px",
			render: (ticket: StaffTicket) =>
				ticket.assignee ? (
					<span className="flex items-center gap-2 text-sm text-text">
						<span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-action-200 text-[10px] font-semibold text-brand-light">
							{ticketUserInitials(ticket.assignee)}
						</span>
						<span className="truncate">
							{ticket.assignee.firstName ?? ticketUserName(ticket.assignee)}
						</span>
					</span>
				) : (
					<span className="flex items-center gap-2 text-sm text-text-meta">
						<span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-dashed border-gray-200 text-xs">
							+
						</span>
						{t("support.staff.assignee.unassigned")}
					</span>
				),
		},
		{
			title: t("support.staff.columns.lastActivity"),
			width: "160px",
			render: (ticket: StaffTicket) => {
				const activity = staffLastActivity(ticket);

				return (
					<div className="flex flex-col">
						<span className="whitespace-nowrap text-sm text-text">
							{formatDistanceToNow(new Date(activity.at), { addSuffix: true })}
						</span>
						<span className="text-[11px] text-text-meta">
							{activity.actor.name ?? t(`support.staff.activity.${activity.actor.kind}`)}
						</span>
					</div>
				);
			},
		},
		{
			title: t("support.staff.columns.age"),
			width: "70px",
			render: (ticket: StaffTicket) => {
				const age = ageParts(ticket.createdAt, now);

				return (
					<span className="whitespace-nowrap text-sm tabular-nums text-text">
						{t(`support.staff.units.${age.unit}`, { value: age.value })}
					</span>
				);
			},
		},
	];

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<Table
				columns={columns}
				data={controller.rows}
				handleChangeRowsPerPage={(limit: number) => {
					controller.setLimit(limit);
					controller.setPage(1);
				}}
				handleFetchPage={controller.setPage}
				handleRowClick={(id: number) =>
					navigate(`${ROUTE_CONFIG.TICKETS}/${id}`, { state: toQueueRouteState(controller.rows) })
				}
				hideFooter
				isFetching={controller.isFetching}
				isLoading={controller.isLoading}
				limit={controller.limit}
				noDataText={t(
					controller.hasFilters ? "support.staff.empty.filtered" : "support.staff.empty.none"
				)}
				overallCount={controller.itemCount}
				page={controller.page}
				totalPages={controller.totalPages}
			/>
		</div>
	);
};
